/**
 * Ask a Question Service
 *
 * Sends contextual questions to the Clinical Quality team via Azure Logic App.
 * Replaces simple mailto: links with structured email that includes patient/facility/audit
 * context and deep links back to the dashboard.
 *
 * Email routing:
 *   From: dataanalytics@eventuswh.com
 *   To:   hr@eventuswh.com
 *   CC:   logged-in user's email
 */

import { toast } from 'sonner'
import type { QuestionContext, PatientMeasureItem } from '@/lib/equip-types'
import { getMeasureDisplayName } from '@/lib/equip-utils'

/**
 * Get a display-friendly status for a measure
 */
function getMeasureStatus(measure: PatientMeasureItem): string {
  if (!measure.isEligible) return 'N/A'
  return measure.isMet ? 'Met' : 'Unmet'
}

/**
 * Build an HTML table row for a measure in the email body
 */
function buildMeasureRow(measure: PatientMeasureItem): string {
  const statusColor = !measure.isEligible
    ? '#6b7280'
    : measure.isMet
      ? '#16a34a'
      : '#dc2626'

  const keyData = measure.clinicalValues
    ?.filter((cv) => cv.label !== 'Status' && cv.value !== '\u2014')
    .map((cv) => `${cv.label}: ${cv.value}`)
    .join(', ') || '\u2014'

  return `
    <tr>
      <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px;">${getMeasureDisplayName(measure.measureName)}</td>
      <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: ${statusColor}; font-weight: 500;">${getMeasureStatus(measure)}</td>
      <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #6b7280;">${keyData}</td>
    </tr>
  `
}

/**
 * Generate HTML email body with question context
 */
function generateEmailBody(
  context: QuestionContext,
  questionText?: string,
  patientMeasures?: PatientMeasureItem[]
): string {
  const { type, identifier, measureName, providerName, providerEmail, year, quarter, facilityName, additionalContext } = context

  // Build deep link back to dashboard
  const params = new URLSearchParams({ providerEmail })
  if (type === 'patient') params.set('patient', identifier)
  const deepLink = `${window.location.origin}/equip/clinician?${params}`

  // Build context list based on type
  let contextItems = `
    <li><strong>Type:</strong> ${type.charAt(0).toUpperCase() + type.slice(1)}</li>
    <li><strong>Identifier:</strong> ${identifier}</li>
  `

  if (measureName) {
    contextItems += `<li><strong>Measure:</strong> ${measureName}</li>`
  }

  contextItems += `
    <li><strong>Quarter:</strong> Q${quarter} ${year}</li>
  `

  if (facilityName) {
    contextItems += `<li><strong>Facility:</strong> ${facilityName}</li>`
  }

  if (additionalContext) {
    Object.entries(additionalContext).forEach(([key, value]) => {
      const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
      contextItems += `<li><strong>${label}:</strong> ${value}</li>`
    })
  }

  // For bulk type, list all IDs
  let bulkSection = ''
  if (type === 'bulk') {
    const ids = identifier.split(',')
    bulkSection = `
      <div style="margin: 16px 0; padding: 12px; background-color: #f5f5f5; border-radius: 4px;">
        <strong>Selected Items (${ids.length}):</strong>
        <div style="margin-top: 8px; max-height: 200px; overflow-y: auto;">
          ${ids.map(id => `<div style="padding: 4px 0;">${id}</div>`).join('')}
        </div>
      </div>
    `
  }

  // Question section
  const questionSection = questionText
    ? `
      <div style="padding: 0 24px 24px 24px;">
        <h2 style="margin: 0 0 12px 0; font-size: 18px; color: #053252;">Question</h2>
        <div style="padding: 16px; background-color: #f9fafb; border-left: 4px solid #053252; border-radius: 4px;">
          <p style="margin: 0; font-size: 14px; white-space: pre-wrap;">${questionText}</p>
        </div>
      </div>
    `
    : `
      <div style="padding: 0 24px 24px 24px;">
        <p style="margin: 0; font-size: 14px; color: #666;">
          <em>No specific question provided \u2014 general inquiry about this patient.</em>
        </p>
      </div>
    `

  // Measure summary table
  let measureSection = ''
  if (patientMeasures && patientMeasures.length > 0) {
    measureSection = `
      <div style="padding: 0 24px 24px 24px;">
        <h2 style="margin: 0 0 12px 0; font-size: 18px; color: #053252;">Measure Summary</h2>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; border-radius: 4px; overflow: hidden;">
          <thead>
            <tr style="background-color: #f3f4f6;">
              <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Measure</th>
              <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Status</th>
              <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Key Data</th>
            </tr>
          </thead>
          <tbody>
            ${patientMeasures.map(buildMeasureRow).join('')}
          </tbody>
        </table>
      </div>
    `
  }

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background-color: #f5f5f5;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
    <!-- Header -->
    <div style="background-color: #053252; color: #ffffff; padding: 24px; text-align: center;">
      <h1 style="margin: 0; font-size: 24px; font-weight: 600;">EQuIP Question</h1>
      <p style="margin: 8px 0 0 0; font-size: 14px; opacity: 0.9;">from ${providerName}</p>
    </div>

    <!-- Question Section -->
    ${questionSection}

    <!-- Context Section -->
    <div style="padding: 24px; border-left: 4px solid #053252; margin: 0 24px 24px 24px; background-color: #f9fafb;">
      <h2 style="margin: 0 0 16px 0; font-size: 18px; color: #053252;">Context</h2>
      <ul style="margin: 0; padding-left: 20px; list-style-type: disc;">
        ${contextItems}
      </ul>
    </div>

    ${bulkSection}

    <!-- Measure Summary -->
    ${measureSection}

    <!-- From Section -->
    <div style="padding: 0 24px 24px 24px;">
      <p style="margin: 0; font-size: 14px; color: #666;">
        <strong>From:</strong> ${providerName} (${providerEmail})
      </p>
    </div>

    <!-- Deep Link -->
    <div style="padding: 0 24px 32px 24px;">
      <a href="${deepLink}" style="display: inline-block; background-color: #053252; color: #ffffff; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: 500; font-size: 14px;">
        View in Dashboard
      </a>
    </div>
  </div>
</body>
</html>
  `.trim()
}

/**
 * Send a question email to the Clinical Quality team
 *
 * Email routing:
 *   From: dataanalytics@eventuswh.com
 *   To:   hr@eventuswh.com
 *   CC:   logged-in user's email (context.providerEmail)
 *
 * @returns true if email sent successfully, false otherwise
 */
export async function askQuestion(
  context: QuestionContext,
  questionText?: string,
  patientMeasures?: PatientMeasureItem[]
): Promise<boolean> {
  const logicAppUrl = import.meta.env.VITE_EQUIP_LOGIC_APP_URL

  if (!logicAppUrl) {
    toast.error('Email service not configured')
    console.error('VITE_EQUIP_LOGIC_APP_URL environment variable not set')
    return false
  }

  try {
    const emailBody = generateEmailBody(context, questionText, patientMeasures)

    const payload = {
      to: 'hr@eventuswh.com',
      from: 'dataanalytics@eventuswh.com',
      cc: context.providerEmail,
      subject: `EQuIP Question - ${context.type} - ${context.identifier}`,
      body: emailBody
    }

    const response = await fetch(logicAppUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (response.ok) {
      toast.success('Question sent to Clinical Quality team')
      return true
    } else {
      const errorText = await response.text().catch(() => 'Unknown error')
      console.error('Failed to send question email:', response.status, errorText)
      toast.error('Failed to send question. Please try again.')
      return false
    }
  } catch (error) {
    console.error('Error sending question email:', error)
    toast.error('Failed to send question. Please try again.')
    return false
  }
}

/**
 * Send a bulk question email for multiple selected items
 * @param ids - Array of patient IDs, facility IDs, or audit IDs
 * @param _itemType - Type of items being questioned (for future use, currently all bulk questions use type='bulk')
 * @param context - Shared context (provider, year, quarter, etc.)
 * @returns true if email sent successfully, false otherwise
 */
export async function askBulkQuestion(
  ids: string[],
  _itemType: 'patient' | 'facility' | 'audit',
  context: Omit<QuestionContext, 'type' | 'identifier'>
): Promise<boolean> {
  const bulkContext: QuestionContext = {
    ...context,
    type: 'bulk',
    identifier: ids.join(',')
  }

  return askQuestion(bulkContext)
}
